import React, { useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import AddIcon from '@mui/icons-material/Add'
import { Tooltip } from '@mui/material'
import { DeleteIcon } from '../../icons/Icons'

import StyledModal from '../../components/StyledModal/Modal'
import StyledTextFieldWithLabel from '../../components/StyledTextFieldWithLabel/StyledTextFieldWithLabel'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'

import { PartsData, TypeAddPartData } from '../../types/parts'
import { OptionItem } from '../../types/common'
import { useAdminGlobalStateContext } from '../../context/AdminGlobalStateContext'

interface Props {
  isModalOpen: boolean
  closeHandler: () => void
  selectedPart: PartsData | null
  addPart?: (partData: TypeAddPartData) => void
  editPart?: (id: number, partData: TypeAddPartData) => void
  isEditMode?: boolean
  isLoading?: boolean
}

const defaultOption: OptionItem = { value: 0, label: 'Select' }

const PartUploadModal = (props: Props) => {
  const { machines: machineOptions } = useAdminGlobalStateContext()

  const [partName, setPartName] = useState('')
  const [isActive, setIsActive] = useState(true)

  const [partTypeOptions, setPartTypeOptions] = useState<OptionItem[]>([])
  const [loadUnloadTimeOptions, setLoadUnloadTimeOptions] = useState<
    OptionItem[]
  >([])

  const [operationMap, setOperationMap] = useState<
    Record<
      number,
      {
        operationType: OptionItem
        machines: OptionItem[]
        machiningTime: string
        loadUnloadTime: OptionItem
      }
    >
  >({})

  // Fetch dropdown data
  useEffect(() => {
    fetch(`${process.env.REACT_APP_ADMIN_BASE_URL}/v1/operation-type`)
      .then((res) => res.json())
      .then((data) => {
        const options = data.data.map((item: any) => ({
          value: item.type,
          label: item.type
        }))
        setPartTypeOptions(options)
      })

    fetch(`${process.env.REACT_APP_ADMIN_BASE_URL}/v1/load-unload-time`)
      .then((res) => res.json())
      .then((data) => {
        const options = data.data.map((item: any) => ({
          value: item.loadUnloadTime,
          label: item.loadUnloadTime
        }))
        setLoadUnloadTimeOptions(options)
      })
  }, [])


  useEffect(() => {
    if (props.isEditMode && props.selectedPart) {
      setPartName(props.selectedPart.name)
      setIsActive(props.selectedPart.isActive)

      setOperationMap({
        1: {
          operationType: {
            value: props.selectedPart.type,
            label: props.selectedPart.type
          },
          machines: [
            {
              value: props.selectedPart.machine,
              label: props.selectedPart.machine
            }
          ],
          machiningTime: props.selectedPart.machiningTime,
          loadUnloadTime: {
            value: props.selectedPart.loadUnloadTime,
            label: props.selectedPart.loadUnloadTime
          }
        }
      })
    }
  }, [props.isEditMode, props.selectedPart])

  const handleAddOperation = () => {
    const keyNum = Object.keys(operationMap).length + 1
    setOperationMap((prev) => ({
      ...prev,
      [keyNum]: {
        operationType: defaultOption,
        machines: [],
        machiningTime: '',
        loadUnloadTime: defaultOption
      }
    }))
  }

  const handleSubmit = () => {
    if (!partName) return

    if (props.isEditMode && props.selectedPart && props.editPart) {
      const op = Object.values(operationMap)[0]
      const machine = op.machines[0]

      props.editPart(props.selectedPart.id, {
        name: partName,
        type: op.operationType.value,
        machine: machine.value,
        machiningTime: op.machiningTime,
        loadUnloadTime: op.loadUnloadTime.value,
        isActive
      })
    } else if (props.addPart) {
      Object.values(operationMap).forEach((op) => {
        op.machines.forEach((machine) => {
          props.addPart &&
            props.addPart({
              name: partName,
              type: op.operationType.value,
              machine: machine.value,
              machiningTime: op.machiningTime,
              loadUnloadTime: op.loadUnloadTime.value,
              isActive
            })
        })
      })
    }

    props.closeHandler()
  }

  return (
    <StyledModal
      width={600}
      loading={props.isLoading}
      isOpen={props.isModalOpen}
      title={props.isEditMode ? 'Edit part' : 'Add part'}
      closeHandler={props.closeHandler}
    >
      <Stack spacing={2} sx={{ width: '100%' }}>
        <StyledTextFieldWithLabel
          label="Part number"
          placeholderText="Enter part number"
          textValue={partName}
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPartName(e.target.value)
          }
          isDisabled={false}
          type="text"
          isMargin
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Add to production list</Typography>
          <Checkbox
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </Box>

        <Typography sx={{ fontWeight: 600 }}>Operation</Typography>

        {Object.entries(operationMap).map(([key, item]) => {
          const index = Number(key)
          return (
            <Box
              key={key}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px'
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>
                    Operation {key}
                  </Typography>

                  <DeleteIcon
                    onClickHandler={() => {
                      setOperationMap((prev) => {
                        const updated = { ...prev }
                        delete updated[index]
                        return updated
                      })
                    }}
                  />
                </Box>

                <StyledSelectField
                  label="Operation type"
                  placeHolderText="Select operation type"
                  selectedValue={item.operationType}
                  selectOptions={partTypeOptions}
                  onChangeHandler={(value: OptionItem) =>
                    setOperationMap((prev) => ({
                      ...prev,
                      [index]: { ...prev[index], operationType: value }
                    }))
                  }
                  isMulti={false}
                  isSearchable
                  isDisabled={false}
                />

                <StyledSelectField
                  label="Machines"
                  placeHolderText="Select machines"
                  selectedValue={item.machines}
                  selectOptions={machineOptions}
                  onChangeHandler={(value: OptionItem[]) =>
                    setOperationMap((prev) => ({
                      ...prev,
                      [index]: { ...prev[index], machines: value }
                    }))
                  }
                  isMulti
                  isSearchable
                  isDisabled={false}
                />

                <StyledTextFieldWithLabel
                  label="Machining time (hh:mm:ss)"
                  placeholderText="Enter machining time"
                  textValue={item.machiningTime}
                  onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOperationMap((prev) => ({
                      ...prev,
                      [index]: {
                        ...prev[index],
                        machiningTime: e.target.value
                      }
                    }))
                  }
                  isDisabled={false}
                  type="text"
                />

                <StyledSelectField
                  label="Load and unload time"
                  placeHolderText="Select load/unload time"
                  selectedValue={item.loadUnloadTime}
                  selectOptions={loadUnloadTimeOptions}
                  onChangeHandler={(value: OptionItem) =>
                    setOperationMap((prev) => ({
                      ...prev,
                      [index]: { ...prev[index], loadUnloadTime: value }
                    }))
                  }
                  isMulti={false}
                  isSearchable
                  isDisabled={false}
                />
              </Stack>
            </Box>
          )
        })}

        <Stack sx={{ alignItems: 'center' }}>
          <Tooltip title="Add operation">
            <Box
              onClick={handleAddOperation}
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                '&:hover': { cursor: 'pointer' }
              }}
            >
              <AddIcon sx={{ color: '#676767', fontSize: '24px' }} />
            </Box>
          </Tooltip>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={props.closeHandler}>
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ color: '#fff' }}
            onClick={handleSubmit}
          >
            {props.isEditMode ? 'Update' : 'Add part'}
          </Button>
        </Stack>
      </Stack>
    </StyledModal>
  )
}

export default PartUploadModal
